import React, { useState, useEffect } from "react";
import { Box, CircularProgress, Button } from "@mui/material";
import { TrendingUp, TrendingDown, Users, Scale, ChevronDown, Map, X, Trophy } from "lucide-react";
import { Typography } from "../../components/typography";
import { AIDisclaimer } from "../../components/ai-disclaimer";
import { useTranslation } from "../../hooks";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import DynamicSelect from "../../components/form-control/form-builder/form-builder-element/dynamic-select";
import "./vendor-ai-summary.scss";

export interface VendorInsightOut {
  vendor_id: number;
  vendor_name: string | null;
  facts: {
    po_created: number;
    po_delivered: number;
    timely_delivery: number;
    missed_delivery: number;
    delivery_date_unknown: number;
    short_shipment_count: number;
    lead_time_kept: number;
    lead_time_missed: number;
    lead_time_unknown: number;
    price_pattern: Array<{
      item_id: number;
      item_name: string;
      first_price: number;
      last_price: number;
      pct_change: number;
      trend: string;
      n: number;
    }>;
    payment_terms_pattern: Record<string, number>;
    rfq_invited: number;
    rfq_responded: number;
    quick_response_items: string[];
    late_response_items: string[];
    no_response_items: string[];
  };
  insight: string;
}

export interface VendorAIProfileProps {
  profileData?: {
    company_id: number;
    vendors: VendorInsightOut[];
  } | null;
  isProfileLoading?: boolean;
  isProfileError?: boolean;
  insightData?: {
    company_id: number;
    vendors: VendorInsightOut[];
    top_recommendations?: Array<{
      vendor_id: number;
      vendor_name: string | null;
      rank: number;
      reason: string;
    }>;
  } | null;
  isInsightLoading?: boolean;
  isInsightError?: boolean;
  onCompare?: (vendorIds: number[]) => void;
}

export const VendorAISummary: React.FC<VendorAIProfileProps> = ({
  profileData,
  isProfileLoading,
  isProfileError,
  insightData,
  isInsightLoading,
  isInsightError,
  onCompare,
}) => {
  const [itemsToShow, setItemsToShow] = useState(3);
  const [selectedVendorOptions, setSelectedVendorOptions] = useState<any[]>([]);
  const [localInsightData, setLocalInsightData] = useState<any>(insightData);
  const { t } = useTranslation();

  const methods = useForm({
    defaultValues: { compare_vendors: { list: [] } },
  });

  const mainVendorId = profileData?.vendors?.[0]?.vendor_id;

  useEffect(() => {
    setLocalInsightData(insightData);
  }, [insightData]);

  useEffect(() => {
    // Clear selected comparison vendors when main vendor changes
    methods.reset({ compare_vendors: { list: [] } });
    setSelectedVendorOptions([]);
    setLocalInsightData(null);
  }, [mainVendorId, methods]);

  const handleCompare = () => {
    if (onCompare && profileData?.vendors?.[0]) {
      const val = methods.getValues("compare_vendors.list");
      const selectedIds: number[] = Array.isArray(val)
        ? val.map((v: any) => Number(v?.value || v?.id || v))
        : val
        ? [Number(typeof val === "object" ? (val as any)?.value : val)]
        : [];

      onCompare(selectedIds.filter(Boolean));
    }
  };

  const handleRemoveVendor = (vendorToRemove: any) => {
    const idToRemove = vendorToRemove?.value || vendorToRemove?.id || vendorToRemove;
    const updated = selectedVendorOptions.filter((v: any) => 
      (v?.value || v?.id || v) !== idToRemove
    ).map(v => v?.value || v?.id || v);
    methods.setValue("compare_vendors.list", updated, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    setSelectedVendorOptions(prev => prev.filter(v => (v?.value || v?.id || v) !== idToRemove));
  };

  if (isProfileLoading) {
    return (
      <Box className="vai__loading">
        <CircularProgress size={28} sx={{ color: "#10b981" }} />
      </Box>
    );
  }

  if (isProfileError) {
    return (
      <Box className="vai__empty">
        <Typography type="s4">Failed to load vendor profile. Please try again.</Typography>
      </Box>
    );
  }

  const vendorData = profileData?.vendors?.[0];

  if (!vendorData) {
    return (
      <Box className="vai__empty">
        <Typography type="s4">No vendor data available.</Typography>
      </Box>
    );
  }

  const facts = vendorData.facts;
  const pricePatterns = facts?.price_pattern ?? [];

  return (
    <FormProvider {...methods}>
      <Box className="vai">

        {/* ── Insight ─────────────────────────────────────── */}
        <Box className="vai__block">
          <Box className="vai__block-title">
            <Box className="vai__badge vai__badge--green">
              <Map size={13} />
              <Typography type="s4" weight="medium">Insight</Typography>
            </Box>
          </Box>
          <Typography type="s4" className="vai__block-body">
            {vendorData.insight || "Vendor has no purchase order or RFQ activity, so its performance and reliability cannot be evaluated at this time."}
          </Typography>
        </Box>

        <Box className="vai__divider" />

        {/* ── Price Patterns ─────────────────────────────── */}
        <Box className="vai__block">
          <Box className="vai__block-title">
            <Box className="vai__badge vai__badge--green">
              <TrendingUp size={13} />
              <Typography type="s4" weight="medium">Price Patterns</Typography>
            </Box>
          </Box>

          {pricePatterns.length > 0 ? (
            <Box className="vai__price-list">
              {pricePatterns.slice(0, itemsToShow).map((pattern, idx) => (
                <Box key={idx} className="vai__price-row">
                  <span className="vai__price-name">{pattern.item_name}</span>
                  <span className={`vai__price-val vai__price-val--${pattern.trend ?? 'flat'}`}>
                    {pattern.trend === "up"
                      ? <TrendingUp size={13} />
                      : pattern.trend === "down"
                      ? <TrendingDown size={13} />
                      : null}
                    {pattern.pct_change >= 0 ? "+" : ""}{pattern.pct_change.toFixed(1)}%
                  </span>
                </Box>
              ))}
              {pricePatterns.length > itemsToShow && (
                <Box className="vai__load-more" onClick={() => setItemsToShow((p) => p + 5)}>
                  <Box className="vai__load-more-pill">
                    {t("common.load_more")} <ChevronDown size={13} />
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Typography type="s5" className="vai__empty-msg">No recent price trends found.</Typography>
          )}
        </Box>

        <Box className="vai__divider" />

        {/* ── Compare Vendors ────────────────────────────── */}
        <Box className="vai__block">
          <Box className="vai__block-title">
            <Box className="vai__badge vai__badge--green">
              <Users size={13} />
              <Typography type="s4" weight="medium">Compare Vendors</Typography>
            </Box>
          </Box>
          <Typography type="s5" className="vai__block-body--muted" sx={{ mt: 1, mb: 2 }}>
            Select multiple vendors to compare and get AI recommendations based on pricing, reliability, and past performance.
          </Typography>

          <Box className="vai__select-wrap">
            <DynamicSelect
              name="list"
              label=""
              placeholder="Search and select vendors"
              formControl={methods.control}
              fieldArrayName="compare_vendors"
              options={[]}
              is_multiselect={true}
              apiType="party"
              customeFilter={{ "status.eq": 1, "&type.eq": "Vendor" }}
              getSelectedData={(data: any) => {
                if (Array.isArray(data)) {
                  setSelectedVendorOptions(data);
                } else if (!data) {
                  setSelectedVendorOptions([]);
                } else {
                  setSelectedVendorOptions([data]);
                }
              }}
            />
          </Box>

          {selectedVendorOptions.length > 0 && (
            <Box className="vai__chips">
              {selectedVendorOptions.map((v: any, idx: number) => (
                <Box key={idx} className="vai__chip">
                  <span>{v?.label || v?.name || v?.value || v}</span>
                  <X
                    size={12}
                    className="vai__chip-close"
                    onClick={() => handleRemoveVendor(v)}
                  />
                </Box>
              ))}
            </Box>
          )}

          <Button
            fullWidth
            variant="contained"
            color="success"
            onClick={handleCompare}
            disabled={isInsightLoading || selectedVendorOptions.length < 2}
            className="vai__compare-btn"
            startIcon={isInsightLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Scale size={17} />}
          >
            {isInsightLoading ? "Comparing…" : "Compare"}
          </Button>
        </Box>

        {/* ── Recommendations ────────────────────────────── */}
        {isInsightError && (
          <Box className="vai__block vai__error">
            <Typography type="s4">Failed to load comparison. Please try again.</Typography>
          </Box>
        )}

        {localInsightData?.top_recommendations && localInsightData.top_recommendations.length > 0 && (
          <>
            <Box className="vai__divider" />
            <Box className="vai__block vai__recs">
              <Box className="vai__recs-header">
                <Box className="vai__recs-icon">
                  <Trophy size={16} />
                </Box>
                <Box>
                  <Typography type="s3" weight="medium" className="vai__recs-title">
                    Top Recommendations
                  </Typography>
                </Box>
              </Box>

              <Box className="vai__recs-list">
                {localInsightData.top_recommendations.map((rec: any, idx: number) => (
                  <Box key={idx} className="vai__rec-item">
                    <Box className="vai__rec-item-header">
                      <Box className={`vai__rec-rank ${idx === 0 ? 'vai__rec-rank--first' : ''}`}>{rec.rank}</Box>
                      {rec.vendor_name && <Typography type="s4" weight="medium" className="vai__rec-name">{rec.vendor_name}</Typography>}
                    </Box>
                    <Typography type="s5" className="vai__rec-reason">{rec.reason}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}

        <AIDisclaimer showIcon={false}/>
      </Box>
    </FormProvider>
  );
};

export default VendorAISummary;
